import { z } from 'zod';

import { MatchingRecord } from '@eluve/utils';

import { VendorTypesEnum } from './types';

type VendorIntegrationTypes = 'API' | 'WEB';

export const VendorTypesLookup = {
  JANE: 'JANE',
  EXPERITY: 'EXPERITY',
  TEBRA: 'TEBRA',
  MOVEMENT_X: 'MOVEMENT_X',
  BLVD: 'BLVD',
  SILOAM: 'SILOAM',
  SUGAR: 'SUGAR',
  ORCHARD: 'ORCHARD',
} as const satisfies MatchingRecord<VendorTypesEnum>;

export const ExternalEhrNoteScoringVendorTypesLookup = {
  SILOAM: 'SILOAM',
} as const satisfies Partial<MatchingRecord<VendorTypesEnum>>;

export const ApiBasedSupportedVendorTypesLookup = {
  BLVD: 'BLVD',
  SILOAM: 'SILOAM',
  SUGAR: 'SUGAR',
  ORCHARD: 'ORCHARD',
} as const satisfies Partial<MatchingRecord<VendorTypesEnum>>;

export const WebBasedSupportedVendorTypesLookup = {
  JANE: 'JANE',
  EXPERITY: 'EXPERITY',
  MOVEMENT_X: 'MOVEMENT_X',
  TEBRA: 'TEBRA',
  SILOAM: 'SILOAM',
  SUGAR: 'SUGAR',
  ORCHARD: 'ORCHARD',
} as const satisfies Partial<MatchingRecord<VendorTypesEnum>>;

export const supportedVendorsSchema = z.nativeEnum(VendorTypesLookup);

export const externalEhrNoteScoringVendorsSchema = z.nativeEnum(
  ExternalEhrNoteScoringVendorTypesLookup,
);

export const supportedApiBasedVendorsSchema = z.nativeEnum(
  ApiBasedSupportedVendorTypesLookup,
);
export const supportedWebBasedVendorsSchema = z.nativeEnum(
  WebBasedSupportedVendorTypesLookup,
);

export type WebBasedSupportedVendors = z.infer<
  typeof supportedWebBasedVendorsSchema
>;
export type ApiBasedSupportedVendors = z.infer<
  typeof supportedApiBasedVendorsSchema
>;

export type SupportedVendors = z.infer<typeof supportedVendorsSchema>;

export const VENDOR_DISPLAY_NAMES: Record<SupportedVendors, string> = {
  JANE: 'Jane',
  EXPERITY: 'Experity',
  TEBRA: 'Tebra',
  MOVEMENT_X: 'MovementX',
  BLVD: 'Boulevard',
  SILOAM: 'Siloam',
  SUGAR: 'Sugar',
  ORCHARD: 'Orchard',
};

export function getVendorDisplayName(vendor: string): string {
  return (
    VENDOR_DISPLAY_NAMES[vendor as SupportedVendors] ??
    vendor.charAt(0).toUpperCase() + vendor.slice(1).toLowerCase()
  );
}

export const VendorIntegrationMapping: Record<
  VendorTypesEnum,
  VendorIntegrationTypes
> = {
  JANE: 'WEB',
  EXPERITY: 'WEB',
  TEBRA: 'WEB',
  MOVEMENT_X: 'WEB',
  BLVD: 'API',
  SILOAM: 'API',
  SUGAR: 'WEB',
  ORCHARD: 'WEB',
} as const satisfies Record<VendorTypesEnum, VendorIntegrationTypes>;

export const isVendorWebBased = (
  vendor: VendorTypesEnum | string,
): vendor is WebBasedSupportedVendors =>
  VendorIntegrationMapping[vendor as VendorTypesEnum] === 'WEB';

export const isWebBasedSupportedVendor = (
  vendor: string,
): vendor is WebBasedSupportedVendors =>
  Object.hasOwn(WebBasedSupportedVendorTypesLookup, vendor);

export const isVendorApiBased = (
  vendor: VendorTypesEnum | string,
): vendor is ApiBasedSupportedVendors =>
  VendorIntegrationMapping[vendor as VendorTypesEnum] === 'API';

export const webhookEventTaskSchema = z.object({
  event: z.string(),
  data: z.record(z.string(), z.unknown()),
  metadata: z.object({
    vendor: supportedApiBasedVendorsSchema,
    domain: z.string(),
    externalEhrId: z.string(),
    tenantId: z.string(),
  }),
});

export type WebhookEventTask = z.infer<typeof webhookEventTaskSchema>;

export type TypedWebhookEventTask<T extends Record<string, unknown>> = Omit<
  WebhookEventTask,
  'data'
> & { data: T };
